class NoelInlineSearch extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.input = this.querySelector('[ref="searchInput"]');
    this.panel = this.querySelector('.nh-search-panel');
    this.native = this.querySelector('predictive-search-component');
    if (!this.input || !this.panel || !this.native) return;
    const signal = this.abort.signal;
    this.panelPointerDown = false;
    this.addEventListener('pointerdown', event => {
      this.panelPointerDown = this.panel.contains(event.target);
    }, { signal });
    this.addEventListener('click', () => {
      this.panelPointerDown = false;
    }, { signal });
    document.addEventListener('pointerup', event => {
      if (!this.panel.contains(event.target)) this.panelPointerDown = false;
    }, { signal });
    document.addEventListener('pointercancel', () => {
      this.panelPointerDown = false;
      if (!this.contains(document.activeElement)) this.close();
    }, { signal });
    this.addEventListener('input', () => {
      if (this.input.value.trim()) this.open();
      else this.close();
    }, { signal });
    this.input.addEventListener('focus', () => {
      if (!this.input.value.trim()) return;
      this.open();
      if (!this.querySelector('.predictive-search-dropdown')) {
        customElements.whenDefined('predictive-search-component').then(() => {
          if (!signal.aborted && this.contains(document.activeElement)) this.native.search({ inputType: 'insertText' });
        });
      }
    }, { signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.input.focus();
        this.close();
      } else if (event.key === 'Tab') {
        event.stopPropagation();
      } else if (event.key === 'Enter' && this.panel.hidden) {
        event.stopPropagation();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (this.input.value.trim()) this.open();
      }
    }, { signal, capture: true });
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.close();
    }, { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (!this.panelPointerDown && !this.contains(document.activeElement)) this.close();
      });
    }, { signal });
    this.observe?.disconnect();
    this.observe = new MutationObserver(() => this.updateResults());
    this.observe.observe(this.native, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
    this.resize?.disconnect();
    this.resize = new ResizeObserver(() => { if (!this.getClientRects().length) this.close(); });
    this.resize.observe(this);
    this.updateResults();
    this.setAttribute('data-enhanced', 'true');
    customElements.whenDefined('predictive-search-component').then(() => {
      if (!signal.aborted && this.input.value.trim() && this.contains(document.activeElement)) {
        this.open();
        this.native.search({ inputType: 'insertText' });
      }
    });
  }
  open() {
    document.querySelectorAll('noel-inline-search').forEach(search => { if (search !== this) search.close?.(); });
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-open', '');
  }
  close() {
    if (!this.panel || !this.input) return;
    this.panelPointerDown = false;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('data-open');
  }
  updateResults() {
    const root = this.native.querySelector('[ref="predictiveSearchResults"]');
    if (!root) return;
    const prefix = this.dataset.resultPrefix;
    const ids = new Map();
    root.querySelectorAll('[id]').forEach(element => {
      if (element.id.startsWith(prefix + '-')) return;
      const id = prefix + '-' + element.id;
      ids.set(element.id, id);
      element.id = id;
    });
    root.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [for]').forEach(element => {
      for (const name of ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for']) {
        if (!element.hasAttribute(name)) continue;
        element.setAttribute(name, element.getAttribute(name).split(' ').map(id => ids.get(id) || id).join(' '));
      }
    });
    root.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card').forEach((item, index) => {
      item.id ||= `${prefix}-option-${index}`;
      item.setAttribute('role', 'option');
    });
    const selected = root.querySelector('[aria-selected="true"]');
    if (selected && !this.panel.hidden) this.input.setAttribute('aria-activedescendant', selected.id);
    else this.input.removeAttribute('aria-activedescendant');
    if (!this.input.value.trim()) this.close();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observe?.disconnect();
    this.resize?.disconnect();
  }
}
if (!customElements.get('noel-inline-search')) customElements.define('noel-inline-search', NoelInlineSearch);
